"use client";

// The sliding pill of the site bar (components/site-nav.tsx). It is the one gesture the homepage bar has and
// this one did not (Jérémy, 19/09: « il faut que tu intègres le bon menu aussi, comme nous avons sur la home
// page »): a single element that flies from item to item, scaling to each one's width on the way.
//
// Why it is its own file. site-nav.tsx must stay a SERVER component — tests/site-chrome.test.tsx greps it for
// "use client", for hooks and for addEventListener — so every line of JavaScript the pill needs lives here, in
// the pill itself. It renders one span inside the first item and binds the pointer from an effect, on the links
// that are already in the DOM around it. The bar stays server-rendered; only this span is client.
//
// What it does NOT do, on purpose. The homepage pill follows whichever section is on screen, and observes the
// page to know it.
// Here the PAGE is the state: the pill rests on the item the bar marks with aria-current, flies to whatever is
// hovered, and comes back when the pointer leaves. Nothing moves while scrolling, and nothing reads the scroll.
//
// Without JavaScript this span never moves, so it is rendered off (data-off) and site-nav.css hides it outright
// under `scripting: none` — where the current page keeps the outline and the rule it has always had.
//
// movePill and fadePill are the homepage's, recopied from components/home-v2/nav.tsx the way site-nav.css
// recopies nav.css (that file is the validated model and is not mine to touch). They are the obvious thing to
// share between the two bars — see menu/rapport-menu.md, which proposes where.
//
// 22/09 — this span now serves TWO bars: the one at the top of the page and the relay capsule that takes over on
// scroll (components/site-fixed-nav.tsx), which wears the homepage's row and therefore the homepage's class names.
// Rather than write the gesture a third time, the three selectors it works on became props. The defaults are the
// top bar's, letter for letter, so <SiteNavPill /> renders and behaves exactly as it did.

import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import "@/components/ds/button.css"; // .ds-edge: the gradient stroke of the pill

type Geometry = { x: number; w: number };

// A CSS time read back from a custom property: the stylesheet is minified, so 320ms comes back as .32s.
const toMs = (time: string, fallback: number) => {
  const value = Number.parseFloat(time);
  if (Number.isNaN(value)) return fallback;
  return time.trim().endsWith("ms") ? value : value * 1000;
};
const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Puts the pill on link `index`. The pill lives in the first item (inset: 0), so on the first link it has no
 * inline style at all and is exactly the server-rendered one. Elsewhere: width + translateX. With `animate`, it
 * flies from where it is drawn now (a flight in progress included) with a scaleX to its new width.
 */
function movePill(
  list: HTMLElement,
  pill: HTMLElement,
  index: number,
  animate: boolean,
  flight: RefObject<Animation | null>,
  geometry: RefObject<Geometry | null>,
  linkSelector: string,
) {
  const home = pill.parentElement;
  const link = list.querySelectorAll<HTMLElement>(linkSelector)[index];
  if (!home || !link) return;

  const origin = home.getBoundingClientRect();
  const transform = getComputedStyle(pill).transform;
  const drawn = transform && transform !== "none" ? new DOMMatrixReadOnly(transform) : new DOMMatrixReadOnly();
  const from = { x: drawn.e, w: (geometry.current?.w ?? origin.width) * drawn.a };

  flight.current?.cancel();
  flight.current = null;

  const rect = link.getBoundingClientRect();
  const to = index === 0 ? { x: 0, w: origin.width } : { x: rect.left - origin.left, w: rect.width };
  if (index === 0) {
    pill.style.removeProperty("width");
    pill.style.removeProperty("transform");
  } else {
    pill.style.width = `${to.w}px`;
    pill.style.transform = `translateX(${to.x}px)`;
  }
  geometry.current = to;

  if (!animate || to.w === 0 || (Math.abs(from.x - to.x) < 0.5 && Math.abs(from.w - to.w) < 0.5)) return;
  const style = getComputedStyle(pill);
  flight.current = pill.animate(
    [{ transform: `translateX(${from.x}px) scaleX(${from.w / to.w})` }, { transform: `translateX(${to.x}px)` }],
    {
      duration: toMs(style.getPropertyValue("--v2-dur-base"), 320),
      easing: style.getPropertyValue("--v2-ease-out").trim() || "ease-out",
    },
  );
}

/**
 * Fades the pill in or out where it stands, over --v2-dur-base: opacity and a slight scale. React has already
 * set the resting state (data-off when hidden); the animation runs from the other state to it, or from the
 * values drawn now if a fade is reversed mid-way, then is dropped so the stylesheet holds the end state.
 */
function fadePill(pill: HTMLElement, show: boolean, fade: RefObject<Animation | null>) {
  const style = getComputedStyle(pill);
  const reversing = fade.current?.playState === "running";
  const on = { opacity: "1", scale: "1" };
  const off = { opacity: "0", scale: "0.9" };
  const drawn = { opacity: style.opacity, scale: style.scale === "none" ? "1" : style.scale };
  fade.current?.cancel();
  const animation = pill.animate([reversing ? drawn : show ? off : on, show ? on : off], {
    duration: toMs(style.getPropertyValue("--v2-dur-base"), 320),
    easing: style.getPropertyValue("--v2-ease-out").trim() || "ease-out",
  });
  fade.current = animation;
  animation.onfinish = () => {
    animation.cancel();
    if (fade.current === animation) fade.current = null;
  };
}

export default function SiteNavPill({
  listSelector = ".v2-sitenav__list",
  linkSelector = ".v2-sitenav__link",
  className = "v2-sitenav__pill ds-edge",
}: {
  listSelector?: string;
  linkSelector?: string;
  className?: string;
} = {}) {
  const pillRef = useRef<HTMLSpanElement>(null);
  const flight = useRef<Animation | null>(null);
  const fade = useRef<Animation | null>(null);
  const geometry = useRef<Geometry | null>(null);
  // Null on the server and until the effect below has read the DOM: the pill ships off and fades in on its item.
  const [page, setPage] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);

  const lit = hovered ?? page;
  const litRef = useRef(lit);
  const shownRef = useRef<number | null>(null);

  // The links are the bar's own, rendered by the server component around this span. The pill reads the current
  // page off the markup (aria-current), so the two can never disagree.
  useEffect(() => {
    const pill = pillRef.current;
    const list = pill?.closest<HTMLElement>(listSelector);
    if (!pill || !list) return;
    const links = [...list.querySelectorAll<HTMLElement>(linkSelector)];
    const current = links.findIndex((link) => link.getAttribute("aria-current") === "page");
    setPage(current >= 0 ? current : null);

    // Touch has no hover: a tap would light an item and leave it lit, since no pointer ever leaves.
    const enter = (event: PointerEvent) => {
      if (event.pointerType === "touch") return;
      setHovered(links.indexOf(event.currentTarget as HTMLElement));
    };
    const leave = (event: PointerEvent) => {
      if (event.pointerType !== "touch") setHovered(null);
    };
    for (const link of links) link.addEventListener("pointerenter", enter);
    list.addEventListener("pointerleave", leave);
    return () => {
      for (const link of links) link.removeEventListener("pointerenter", enter);
      list.removeEventListener("pointerleave", leave);
    };
  }, [listSelector, linkSelector]);

  // Layout effect: the pill is placed (and its fade started) before the browser paints the new data-off state.
  useLayoutEffect(() => {
    const pill = pillRef.current;
    const list = pill?.closest<HTMLElement>(listSelector);
    if (!pill || !list) return;
    const was = shownRef.current;
    shownRef.current = lit;
    litRef.current = lit;
    const motion = !reducedMotion();
    if (lit === null) {
      if (was !== null && motion) fadePill(pill, false, fade);
      return;
    }
    // From one item to another it flies; coming back from hidden it is put in place, then fades in.
    movePill(list, pill, lit, was !== null && motion, flight, geometry, linkSelector);
    if (was === null && motion) fadePill(pill, true, fade);
  }, [lit, listSelector, linkSelector]);

  // Fonts arriving, the window resized, the phone panel opening: put the pill back on its item without animating.
  useEffect(() => {
    const pill = pillRef.current;
    const list = pill?.closest<HTMLElement>(listSelector);
    if (!pill || !list || !("ResizeObserver" in window)) return;
    const ro = new ResizeObserver(() => {
      if (litRef.current !== null) movePill(list, pill, litRef.current, false, flight, geometry, linkSelector);
    });
    ro.observe(list);
    return () => ro.disconnect();
  }, [listSelector, linkSelector]);

  return (
    <span
      ref={pillRef}
      className={className}
      aria-hidden="true"
      data-off={lit === null ? "" : undefined}
    />
  );
}
